/**
 * Commander API - File operations via Brain commands
 */

import api from './api';
import type { FileInfo, DirListing, UploadSession } from '../types/commander';

const CHUNK_SIZE = 1024 * 1024; // 1MB chunks

// =====================
// Directory Operations
// =====================

export async function listDirectory(disk: string, path: string): Promise<DirListing> {
  const response = await api.post('/brain/command', {
    command: 'dir.list',
    params: { disk, path }
  });

  if (response.data.error) {
    throw new Error(response.data.error);
  }

  return {
    path,
    entries: response.data.entries || []
  };
}

export async function createDirectory(disk: string, path: string): Promise<FileInfo> {
  const response = await api.post('/brain/command', {
    command: 'dir.create',
    params: { disk, path }
  });

  if (response.data.error) {
    throw new Error(response.data.error);
  }

  return response.data;
}

export async function deleteDirectory(disk: string, path: string, recursive: boolean = false): Promise<void> {
  const response = await api.post('/brain/command', {
    command: 'dir.delete',
    params: { disk, path, recursive }
  });

  if (response.data.error) {
    throw new Error(response.data.error);
  }
}

// =====================
// File Operations
// =====================

export async function getFileInfo(disk: string, path: string): Promise<FileInfo> {
  const response = await api.post('/brain/command', {
    command: 'file.info',
    params: { disk, path }
  });

  if (response.data.error) {
    throw new Error(response.data.error);
  }

  return response.data;
}

export async function deleteFile(disk: string, path: string): Promise<void> {
  const response = await api.post('/brain/command', {
    command: 'file.delete',
    params: { disk, path }
  });

  if (response.data.error) {
    throw new Error(response.data.error);
  }
}

export async function moveFile(disk: string, from: string, to: string): Promise<FileInfo> {
  const response = await api.post('/brain/command', {
    command: 'file.move',
    params: { disk, from, to }
  });

  if (response.data.error) {
    throw new Error(response.data.error);
  }

  return response.data;
}

export async function copyFile(disk: string, from: string, to: string): Promise<FileInfo> {
  const response = await api.post('/brain/command', {
    command: 'file.copy',
    params: { disk, from, to }
  });

  if (response.data.error) {
    throw new Error(response.data.error);
  }

  return response.data;
}

export async function renameFile(disk: string, path: string, newName: string): Promise<FileInfo> {
  const parentDir = path.substring(0, path.lastIndexOf('/')) || '/';
  const newPath = `${parentDir}/${newName}`;
  return moveFile(disk, path, newPath);
}

// =====================
// Upload Operations
// =====================

async function toBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove data URL prefix
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export async function uploadFile(
  disk: string,
  path: string,
  file: File,
  onProgress?: (progress: number) => void
): Promise<FileInfo> {
  // For small files (< 50MB), use simple upload
  if (file.size <= 50 * 1024 * 1024) {
    return uploadFileSimple(disk, path, file, onProgress);
  }

  // For large files, use chunked upload
  return uploadFileChunked(disk, path, file, onProgress);
}

async function uploadFileSimple(
  disk: string,
  path: string,
  file: File,
  onProgress?: (progress: number) => void
): Promise<FileInfo> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('disk', disk);
  formData.append('path', path);

  const response = await api.post('/files/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    onUploadProgress: (event) => {
      if (event.total && onProgress) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    }
  });

  if (response.data.error) {
    throw new Error(response.data.error);
  }

  return response.data;
}

async function uploadFileChunked(
  disk: string,
  path: string,
  file: File,
  onProgress?: (progress: number) => void
): Promise<FileInfo> {
  // 1. Start upload session
  const startResponse = await api.post('/files/upload/start', {
    disk,
    path,
    filename: file.name,
    total_size: file.size
  });

  const sessionId = startResponse.data.session_id;
  const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

  // 2. Upload chunks
  for (let i = 0; i < totalChunks; i++) {
    const start = i * CHUNK_SIZE;
    const end = Math.min(start + CHUNK_SIZE, file.size);
    const chunk = file.slice(start, end);
    const base64Data = await toBase64(chunk);

    await api.post('/files/upload/chunk', {
      session_id: sessionId,
      chunk_index: i,
      data: base64Data
    });

    if (onProgress) {
      onProgress(Math.round(((i + 1) / totalChunks) * 100));
    }
  }

  // 3. Finish upload
  const finishResponse = await api.post('/files/upload/finish', {
    session_id: sessionId
  });

  if (finishResponse.data.error) {
    throw new Error(finishResponse.data.error);
  }

  return finishResponse.data;
}

// =====================
// Download Operations
// =====================

export async function downloadFile(disk: string, path: string, filename: string): Promise<void> {
  const response = await api.get('/files/download', {
    params: { disk, path },
    responseType: 'blob'
  });

  // Create download link
  const url = URL.createObjectURL(response.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// =====================
// Preview Operations
// =====================

export async function getPreview(disk: string, path: string, size: number = 256): Promise<string> {
  const response = await api.get('/files/preview', {
    params: { disk, path, size },
    responseType: 'blob'
  });

  return URL.createObjectURL(response.data);
}

export async function getFileContent(disk: string, path: string): Promise<string> {
  const response = await api.post('/brain/command', {
    command: 'file.read',
    params: { disk, path }
  });

  if (response.data.error) {
    throw new Error(response.data.error);
  }

  // Decode base64 content
  return atob(response.data.content);
}
